import { apiPaths } from 'api/kinerja/kepala-sekolah/config'
import { useKinerjaApi, useKinerjaMutationApi } from 'api/kinerja/utils/api'
import { useMutationApi, useQueryApi } from 'utils/api'

import type { MultiPeriodPayload } from 'types/kinerja'
import type { CustomError } from 'types/custom-error'
import type { CommonResponse } from 'types/api'
import type { PelaksanaanError } from 'types/kinerja-ks-pelaksanaan'
import type {
  TPayloadPenilaianForDelivery,
  TPenilaianFilterStatus,
  TPenilaianTujuanState,
  TResponsePenilaian,
  TResponsePenilaianForDelivery,
  TResponsePenilaianSummary,
  TResponsePenilaianCount,
  TPayloadPenilaian,
  TParamsApproveBuktiKinerja,
  ResponseGetPenilaianKsByPsV2,
  ParamsBulkOverridePenilaianKsByPs,
  ResponseGetPenilaianGuruByPsV2,
  ResponseGetDetailPenilaianKSByPSV2,
  ParamsBulkOverridePenilaianGuruByPs,
  ResponseGetDetailPenilaianGuruByPSV2,
} from 'types/kinerja-ps'
import type { UseMutationOptions, UseQueryOptions } from 'react-query'

export const API_GET_KS_PENILAIAN_BY_PS_QUERY_KEY =
  'API_GET_KS_PENILAIAN_BY_PS_QUERY_KEY'

export const API_GET_KS_PENILAIAN_COUNT_BY_PS_QUERY_KEY =
  'API_GET_KS_PENILAIAN_COUNT_BY_PS_QUERY_KEY'

export const API_GET_KS_PENILAIAN_BY_PS_SUMMARY_QUERY_KEY =
  'API_GET_KS_PENILAIAN_BY_PS_SUMMARY_QUERY_KEY'

export const API_GET_KS_PENILAIAN_BY_PS_SUMMARY_FOR_GURU_QUERY_KEY =
  'API_GET_KS_PENILAIAN_BY_PS_SUMMARY_FOR_GURU_QUERY_KEY'

type TPenilaianSummaryPsProp = {
  state?: TPenilaianTujuanState
}

type TPenilaianPsProp = TPenilaianSummaryPsProp & {
  name?: string
  status?: TPenilaianFilterStatus[]
  confirmedOnly?: boolean
}

export function useApiGetPenilaianPs(
  params: TPenilaianPsProp,
  opts?: UseQueryOptions<TResponsePenilaian>
) {
  const nameParam = params?.name || undefined

  const status: TPenilaianFilterStatus[] = params?.status || ['ALL']
  const statusParam = status ? status?.join(',') : undefined

  const stateParam = params?.state || undefined

  // Intentionally exclude `false` from the query string so the API can handle it according to its default behavior
  const confirmedOnlyParam = params?.confirmedOnly === true || undefined

  return useKinerjaApi<TResponsePenilaian>(apiPaths.penilaianPengawasSekolah, {
    axiosConfig: {
      params: {
        name: nameParam,
        statusPengiriman: statusParam,
        state: stateParam,
        confirmedOnly: confirmedOnlyParam,
      },
    },
    queryConfig: {
      useErrorBoundary: false,
      ...opts,
    },
    queryKey: [
      API_GET_KS_PENILAIAN_BY_PS_QUERY_KEY,
      nameParam,
      statusParam,
      stateParam,
      confirmedOnlyParam,
    ],
  })
}

export function useApiGetPenilaianCountPs(
  opts?: UseQueryOptions<TResponsePenilaianCount>
) {
  return useKinerjaApi<TResponsePenilaianCount>(
    `${apiPaths.penilaianPengawasSekolah}/count`,
    {
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      queryKey: [API_GET_KS_PENILAIAN_COUNT_BY_PS_QUERY_KEY],
    }
  )
}

export function useApiGetPenilaianSummaryPs(
  params: TPenilaianSummaryPsProp,
  opts?: UseQueryOptions<TResponsePenilaianSummary>
) {
  const stateParam = params?.state || undefined

  return useKinerjaApi<TResponsePenilaianSummary>(
    `${apiPaths.penilaianPengawasSekolah}/summary`,
    {
      axiosConfig: {
        params: {
          state: stateParam,
        },
      },
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      queryKey: [API_GET_KS_PENILAIAN_BY_PS_SUMMARY_QUERY_KEY, stateParam],
    }
  )
}
export function useApiPostPenilaianForDelivery(
  opts?: UseMutationOptions<
    CommonResponse<TResponsePenilaianForDelivery>,
    PelaksanaanError,
    TPayloadPenilaianForDelivery
  >
) {
  return useKinerjaMutationApi(apiPaths.penilaianPengawasSekolah, {
    axiosConfig: {
      method: 'POST',
    },
    mutationConfig: {
      useErrorBoundary: false,
      ...opts,
    },
  })
}

export function useApiPatchPenilaianPs(
  opts?: UseMutationOptions<
    CommonResponse<any>,
    PelaksanaanError,
    TPayloadPenilaian
  >
) {
  return useKinerjaMutationApi(
    `${apiPaths.penilaianPengawasSekolah}/override`,
    {
      axiosConfig: {
        method: 'PATCH',
      },
      mutationConfig: {
        useErrorBoundary: false,
        ...opts,
      },
    }
  )
}

export function useAPIApproveBuktiKinerja(
  opts?: UseMutationOptions<unknown, CustomError, TParamsApproveBuktiKinerja>
) {
  return useKinerjaMutationApi<
    unknown,
    CustomError,
    TParamsApproveBuktiKinerja
  >(`${apiPaths.pelaksanaanPengawasSekolah}/competency-improvement/approve`, {
    axiosConfig: {
      method: 'PATCH',
    },
    mutationConfig: {
      ...opts,
    },
  })
}

const API_PATH_PENILAIAN_KS_BY_PS_V2 = `${apiPaths.pengawasSekolahV2}/penilaian`

export function getPenilaianKsByPsV2QueryKey(params: MultiPeriodPayload) {
  const { skpYear, skpPeriod } = params
  return [API_PATH_PENILAIAN_KS_BY_PS_V2, skpYear, skpPeriod]
}

export function useApiGetPenilaianKsByPsV2({
  params,
  options,
}: {
  params: MultiPeriodPayload
  options?: UseQueryOptions<ResponseGetPenilaianKsByPsV2>
}) {
  const { skpYear, skpPeriod } = params

  const defaultQueryKey = getPenilaianKsByPsV2QueryKey(params)
  const { queryKey = defaultQueryKey, ...restOptions } = options

  return useQueryApi<ResponseGetPenilaianKsByPsV2>(
    API_PATH_PENILAIAN_KS_BY_PS_V2,
    {
      axiosConfig: {
        params: {
          skp_year: skpYear,
          skp_period: skpPeriod,
        },
      },
      queryConfig: {
        ...restOptions,
      },
      queryKey,
    }
  )
}

export function useApiBulkOverridePenilaianKsByPs(
  periodParams: MultiPeriodPayload,
  opts?: UseMutationOptions<unknown, Error, ParamsBulkOverridePenilaianKsByPs>
) {
  return useMutationApi(
    `${apiPaths.pengawasSekolah}/penilaian/override/bulk?skp_period=${periodParams.skpPeriod}&skp_year=${periodParams.skpYear}`,
    {
      axiosConfig: {
        method: 'PATCH',
      },
      mutationConfig: {
        ...opts,
      },
    }
  )
}

const API_PATH_PENILAIAN_GURU_BY_PS_V2 = `${apiPaths.guruV2}/ps/penilaian/plt-ks`

export function getPenilaianGuruByPsV2QueryKey(params: MultiPeriodPayload) {
  const { skpYear, skpPeriod } = params
  return [API_PATH_PENILAIAN_GURU_BY_PS_V2, skpYear, skpPeriod]
}
export function useApiGetPenilaianGuruByPsV2({
  params,
  options,
}: {
  params: MultiPeriodPayload
  options?: UseQueryOptions<ResponseGetPenilaianGuruByPsV2>
}) {
  const { skpYear, skpPeriod } = params

  const defaultQueryKey = getPenilaianGuruByPsV2QueryKey(params)
  const { queryKey = defaultQueryKey, ...restOptions } = options

  return useQueryApi<ResponseGetPenilaianGuruByPsV2>(
    API_PATH_PENILAIAN_GURU_BY_PS_V2,
    {
      axiosConfig: {
        params: {
          skp_year: skpYear,
          skp_period: skpPeriod,
        },
      },
      queryConfig: {
        ...restOptions,
      },
      queryKey,
    }
  )
}

export function useApiBulkOverridePenilaianGuruByPs(
  periodParams: MultiPeriodPayload,
  opts?: UseMutationOptions<unknown, Error, ParamsBulkOverridePenilaianGuruByPs>
) {
  return useMutationApi(
    `${apiPaths.guru}/ps/penilaian/plt-ks/override/bulk?skp_period=${periodParams.skpPeriod}&skp_year=${periodParams.skpYear}`,
    {
      axiosConfig: {
        method: 'PATCH',
      },
      mutationConfig: {
        ...opts,
      },
    }
  )
}

const API_PATH_DETAIL_PENILAIAN_KS_BY_PS_V2 = `${apiPaths.pengawasSekolah}/penilaian/detail`

export function getDetailPenilaianKSByPSV2QueryKey(
  params: MultiPeriodPayload & { id: string }
) {
  const { skpYear, skpPeriod, id } = params
  return [API_PATH_DETAIL_PENILAIAN_KS_BY_PS_V2, skpYear, skpPeriod, id]
}
export function useApiGetDetailPenilaianKSByPSV2({
  params,
  options = {},
}: {
  params: MultiPeriodPayload & { id: string }
  options?: UseQueryOptions<ResponseGetDetailPenilaianKSByPSV2>
}) {
  const { skpYear, skpPeriod, id } = params

  const defaultQueryKey = getDetailPenilaianKSByPSV2QueryKey(params)
  const { queryKey = defaultQueryKey, ...restOptions } = options

  return useQueryApi<ResponseGetDetailPenilaianKSByPSV2>(
    API_PATH_DETAIL_PENILAIAN_KS_BY_PS_V2,
    {
      axiosConfig: {
        params: {
          id,
          skp_year: skpYear,
          skp_period: skpPeriod,
        },
      },
      queryConfig: {
        ...restOptions,
      },
      queryKey,
    }
  )
}

const API_PATH_DETAIL_PENILAIAN_GURU_BY_PS_V2 = `${apiPaths.guru}/ps/penilaian/plt-ks/detail`

export function getDetailPenilaianGuruByPSV2QueryKey(
  params: MultiPeriodPayload & { ptkId: string }
) {
  const { skpYear, skpPeriod, ptkId } = params
  return [API_PATH_DETAIL_PENILAIAN_GURU_BY_PS_V2, skpYear, skpPeriod, ptkId]
}
export function useApiGetDetailPenilaianGuruByPSV2({
  params,
  options = {},
}: {
  params: MultiPeriodPayload & { ptkId: string }
  options?: UseQueryOptions<ResponseGetDetailPenilaianGuruByPSV2>
}) {
  const { skpYear, skpPeriod, ptkId } = params

  const defaultQueryKey = getDetailPenilaianGuruByPSV2QueryKey(params)
  const { queryKey = defaultQueryKey, ...restOptions } = options

  return useQueryApi<ResponseGetDetailPenilaianGuruByPSV2>(
    API_PATH_DETAIL_PENILAIAN_GURU_BY_PS_V2,
    {
      axiosConfig: {
        params: {
          ptkId,
          skp_year: skpYear,
          skp_period: skpPeriod,
        },
      },
      queryConfig: {
        ...restOptions,
      },
      queryKey,
    }
  )
}
